/**
 * Keeps the header's open-panel geometry alive for as long as the panel takes to LEAVE.
 *
 * THE PROBLEM THIS EXISTS FOR.
 *
 * The BRWD panel is Horizon's `position: absolute; width: 100%; left: 0` submenu, and the nav it
 * hangs off — `.menu-list` — is a 455px pill group that is `position: relative` and carries
 * `backdrop-filter: blur(90px)`. Either of those alone makes it the containing block for that
 * submenu AND for the `position: fixed` claim strips inside it, which is why
 * `snippets/brwd-menu-styles.liquid` drops both (plus `contain: layout` on `header-component`, plus
 * the two underlays) while the panel is open. That relaxation is what lets the panel be 1430 wide
 * and the strips reach the bottom of the viewport.
 *
 * Every one of those rules is keyed on `aria-expanded='true'`, and `header-menu` flips that to
 * `false` on the frame the tab is clicked — while the panel still has a 200ms fade to run and the
 * strips a 300ms one. So the containing block came back MID-FADE and everything still painting
 * snapped into the pill group. Measured at 1440x812, 100ms into the close: the panel went 1430 ->
 * 455 and its product cards 226 -> 64 (reported as "the cards go small"), and the strips jumped
 * from `0,718 1440x94` to `487,470 455x94` — two green bands straight across the closing panel.
 *
 * WHY IT IS JAVASCRIPT.
 *
 * Neither `contain` nor `backdrop-filter` nor `position` can be transitioned, so CSS cannot hold
 * the relaxation for the length of the fade; and CSS has no selector for "this is on its way out",
 * which is precisely the state that needs describing. So the state is written here as
 * `data-brwd-menu-closing` ON THE TRIGGER ITSELF — the same element the CSS already keys on, so
 * each rule grows one alternative next to its `[aria-expanded='true']` and nothing moves.
 *
 * It holds ONLY the geometry rules. The reveal rules (the panel's and the strips' own
 * opacity/visibility) deliberately do NOT match it: they are the fade, and the fade is the thing
 * that should still happen.
 *
 * A plain listener rather than a custom element, matching `assets/brwd-menu-scroll-close.js` and
 * `assets/auto-close-details.js` — this theme's existing answer to a global behaviour with no UI of
 * its own. It is loaded from `snippets/scripts.liquid` for the same reason those are: the header
 * arrives through Section Rendering, where an injected <script> is in the DOM but never executes,
 * and this has no custom element to re-upgrade.
 */
(function brwdMenuClosingState() {
  const TRIGGER = '.menu-list__link--brwd-panel';
  const ATTR = 'data-brwd-menu-closing';

  /**
   * The elements whose exit animation the hold has to outlast. The background image is a pseudo
   * element on the section, so it is read as one rather than queried.
   *
   * @type {[string, string | null][]}
   */
  const EXITING = [
    ['.menu-list__list-item--brwd-panel > .menu-list__submenu', null],
    ['.brwd-menu-marquee', null],
    ['.header-section', '::before'],
  ];

  /** @type {WeakMap<Element, number>} */
  const timers = new WeakMap();

  /**
   * Milliseconds in the longest `transition-duration + transition-delay` pair of a computed style.
   * Both are comma separated lists of the same length, one entry per transitioned property.
   *
   * @param {CSSStyleDeclaration} styles
   * @returns {number}
   */
  function longestTransitionMs(styles) {
    const toMs = (/** @type {string} */ value) => {
      const seconds = parseFloat(value);
      if (!Number.isFinite(seconds)) return 0;
      return value.trim().endsWith('ms') ? seconds : seconds * 1000;
    };

    const durations = styles.transitionDuration.split(',');
    const delays = styles.transitionDelay.split(',');

    return durations.reduce((longest, duration, index) => {
      return Math.max(longest, toMs(duration) + toMs(delays[index] ?? '0s'));
    }, 0);
  }

  /**
   * How long this particular close will take. Read from the live computed styles rather than
   * hardcoded, so it cannot drift from the stylesheet — and read AFTER the attribute is set, so it
   * is the closing state's own timings, including the `transition: none` that
   * `prefers-reduced-motion` puts there.
   *
   * @param {Element} scope
   * @returns {number}
   */
  function exitDurationMs(scope) {
    let longest = 0;

    for (const [selector, pseudo] of EXITING) {
      // `querySelectorAll` never returns the scope itself, and one of these selectors IS the scope
      // -- the background image is a pseudo element on the section this is scoped to.
      const elements = scope instanceof Element && scope.matches(selector) ? [scope] : scope.querySelectorAll(selector);

      for (const element of elements) {
        longest = Math.max(longest, longestTransitionMs(getComputedStyle(element, pseudo)));
      }
    }

    return longest;
  }

  /** @param {Element} trigger */
  function release(trigger) {
    clearTimeout(timers.get(trigger));
    timers.delete(trigger);
    trigger.removeAttribute(ATTR);
  }

  /** @param {Element} trigger */
  function hold(trigger) {
    clearTimeout(timers.get(trigger));
    trigger.setAttribute(ATTR, '');

    // The section, not the trigger: the strips and the background belong to it, not to the tab.
    const scope = trigger.closest('.header-section') ?? document;

    // One frame of cushion so the hold outlasts the transition rather than ending on its last
    // frame, where the snap would still be visible for a paint.
    timers.set(
      trigger,
      setTimeout(() => release(trigger), exitDurationMs(scope) + 50)
    );
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const trigger = record.target;
      if (!(trigger instanceof Element) || !trigger.matches(TRIGGER)) continue;

      // Reopening ends the hold immediately: the open state's own rules take over, and leaving a
      // stale attribute behind would keep the relaxation on a header that is not closing.
      if (trigger.getAttribute('aria-expanded') === 'true') release(trigger);
      else if (record.oldValue === 'true') hold(trigger);
    }
  });

  // Document-wide and subtree, because the header is replaced wholesale by Section Rendering and a
  // node-scoped observer would be watching a detached tab afterwards.
  observer.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-expanded'],
    attributeOldValue: true,
  });
})();
